import React, { useState, useEffect } from 'react';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import './index.css';

const Toast = ({
  message,
  type = 'success',
  duration = 3000,
  onClose,
  position = 'top-right',
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose && onClose();
    }, 300); // Wait for exit animation
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined className='toast-icon success' />;
      case 'error':
        return <ExclamationCircleOutlined className='toast-icon error' />;
      case 'warning':
        return <ExclamationCircleOutlined className='toast-icon warning' />;
      case 'info':
        return <InfoCircleOutlined className='toast-icon info' />;
      default:
        return <CheckCircleOutlined className='toast-icon success' />;
    }
  };

  const getTypeClass = () => {
    return `toast toast-${type}`;
  };

  if (!isVisible) return null;

  return (
    <div className={`${getTypeClass()} toast-${position}`}>
      <div className='toast-content'>
        {getIcon()}
        <span className='toast-message'>{message}</span>
        <button className='toast-close' onClick={handleClose}>
          <CloseOutlined />
        </button>
      </div>
    </div>
  );
};

// Simple Toast Manager for global use
class ToastManager {
  constructor() {
    this.toasts = [];
    this.container = null;
    // Action callback registry keyed by toast id. The DOM uses
    // data-toast-action-id to reference back to the JS callback when
    // the action button is clicked — necessary because renderToast
    // uses innerHTML which strips JSX/closure references.
    this.actionCallbacks = new Map();
    this.init();
  }

  init() {
    // Create toast container if it doesn't exist
    if (!this.container) {
      this.container = document.createElement('div');
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
      // Delegated click handler — survives innerHTML re-renders and
      // routes action-button clicks back to the registered callback.
      // Close-button clicks remain wired via inline onclick (kept for
      // backwards compatibility with the existing markup).
      this.container.addEventListener('click', (e) => {
        const actionBtn = e.target.closest('[data-toast-action-id]');
        if (actionBtn) {
          const id = Number(actionBtn.getAttribute('data-toast-action-id'));
          const cb = this.actionCallbacks.get(id);
          if (cb) {
            try {
              cb();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[toast] action callback threw:', err);
            }
            // Dismiss the toast after the action fires so the
            // trainer doesn't accidentally re-trigger it.
            this.actionCallbacks.delete(id);
            const wrapper = actionBtn.closest('.toast-wrapper');
            if (wrapper && wrapper.parentNode) wrapper.remove();
          }
        }
      });
    }
  }

  show(options) {
    const toastId = Date.now() + Math.floor(Math.random() * 1000);
    const toast = {
      id: toastId,
      ...options,
    };

    if (options.action && typeof options.action.onClick === 'function') {
      this.actionCallbacks.set(toastId, options.action.onClick);
    }

    this.toasts.push(toast);
    this.renderToast(toast);

    return toastId;
  }

  success(message, options = {}) {
    return this.show({ message, type: 'success', ...options });
  }

  error(message, options = {}) {
    return this.show({ message, type: 'error', ...options });
  }

  warning(message, options = {}) {
    return this.show({ message, type: 'warning', ...options });
  }

  info(message, options = {}) {
    return this.show({ message, type: 'info', ...options });
  }

  remove(id) {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.actionCallbacks.delete(id);
    this.renderToasts();
  }

  clear() {
    this.toasts = [];
    this.actionCallbacks.clear();
    this.renderToasts();
  }

  // Minimal HTML escape so user-supplied content can't break out of
  // the toast markup. Existing callers pass static strings, but
  // future action labels / messages might be dynamic.
  escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  renderToast(toast) {
    const toastElement = document.createElement('div');
    toastElement.className = 'toast-wrapper';

    // Render an action link (e.g., "Stop auto-applying") when provided.
    // Click is wired via the delegated handler in init() — the
    // callback is registered under the toast id at show() time.
    const actionHtml = toast.action
      ? `<button class="toast-action" data-toast-action-id="${toast.id}">${this.escapeHtml(
          toast.action.label
        )}</button>`
      : '';

    // Use a longer auto-dismiss when an action is present so trainer
    // has time to read + click. Default stays 3000ms for plain toasts.
    const duration = toast.duration || (toast.action ? 6000 : 3000);

    toastElement.innerHTML = `
      <div class="toast toast-${toast.type} toast-${
      toast.position || 'top-right'
    }">
        <div class="toast-content">
          <span class="toast-icon ${toast.type}">
            ${this.getIconHTML(toast.type)}
          </span>
          <span class="toast-message">${this.escapeHtml(toast.message)}</span>
          ${actionHtml}
          <button class="toast-close" onclick="this.parentElement.parentElement.remove()">
            ✕
          </button>
        </div>
      </div>
    `;

    this.container.appendChild(toastElement);

    // Auto-remove after duration. Also clear the registered callback
    // so the registry doesn't leak entries for dismissed toasts.
    setTimeout(() => {
      if (toastElement.parentNode) {
        toastElement.remove();
      }
      this.actionCallbacks.delete(toast.id);
    }, duration);
  }

  getIconHTML(type) {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return '✓';
    }
  }

  renderToasts() {
    if (this.container) {
      this.container.innerHTML = '';
      this.toasts.forEach((toast) => this.renderToast(toast));
    }
  }
}

// Create global instance
const toast = new ToastManager();

export default Toast;
export { toast };
